import type { AxiosRequestConfig } from "axios";
import { getAxiosInstance } from "./axios-instance";

type BaseUrlKey =
  | "apiUrl"
  | "aiUrl"
  | "fileuploadUrl"
  | "platformUrl"
  | "platformServiceUrl"
  | "signalRUrl"
  | "dfdApiUrl";

interface ApiRequestConfig<TData = any> {
  url: string;
  params?: Record<string, any>;
  data?: TData;
  baseUrlKey?: BaseUrlKey;
  accessToken?: string;
  totalParts?: string;
  axiosConfig?: AxiosRequestConfig;
}

const apiService = {
  get: async (
    arg1: string | ApiRequestConfig,
    params: Record<string, any> = {},
    baseUrlKey: BaseUrlKey = "apiUrl",
    accessToken?: string,
  ) => {
    if (typeof arg1 === "object") {
      const {
        url,
        params = {},
        baseUrlKey = "apiUrl",
        accessToken,
        axiosConfig,
      } = arg1;

      const axiosInstance = await getAxiosInstance(baseUrlKey, accessToken);

      const options: any = {
        params,
        ...(baseUrlKey === "platformServiceUrl" && {
          responseType: "arraybuffer",
        }),
        ...(baseUrlKey === "fileuploadUrl" && {
          responseType: "blob",
        }),
        ...axiosConfig,
      };

      return axiosInstance.get(url, options);
    }

    const axiosInstance = await getAxiosInstance(baseUrlKey, accessToken);

    const options: any = { params };
    if (baseUrlKey === "platformServiceUrl") {
      options.responseType = "arraybuffer";
    }

    return axiosInstance.get(arg1, options);
  },

  post: async (
    arg1: string | ApiRequestConfig,
    data?: any,
    baseUrlKey: BaseUrlKey = "apiUrl",
    accessToken?: string,
    totalParts?: string,
  ) => {
    if (typeof arg1 === "object") {
      const {
        url,
        data,
        baseUrlKey = "apiUrl",
        accessToken,
        totalParts,
        axiosConfig,
      } = arg1;

      const axiosInstance = await getAxiosInstance(
        baseUrlKey,
        accessToken,
        totalParts,
      );
      return axiosInstance.post(url, data, axiosConfig);
    }

    const axiosInstance = await getAxiosInstance(
      baseUrlKey,
      accessToken,
      totalParts,
    );
    return axiosInstance.post(arg1, data);
  },

  put: async (
    arg1: string | ApiRequestConfig,
    data?: any,
    baseUrlKey: BaseUrlKey = "apiUrl",
  ) => {
    if (typeof arg1 === "object") {
      const {
        url,
        data,
        baseUrlKey = "apiUrl",
        accessToken,
        axiosConfig,
      } = arg1;

      const axiosInstance = await getAxiosInstance(baseUrlKey, accessToken);
      return axiosInstance.put(url, data, axiosConfig);
    }

    const axiosInstance = await getAxiosInstance(baseUrlKey);
    return axiosInstance.put(arg1, data);
  },

  delete: async (
    arg1: string | ApiRequestConfig,
    data?: any,
    baseUrlKey: BaseUrlKey = "apiUrl",
  ) => {
    if (typeof arg1 === "object") {
      const {
        url,
        data,
        baseUrlKey = "apiUrl",
        accessToken,
        axiosConfig,
      } = arg1;

      const axiosInstance = await getAxiosInstance(baseUrlKey, accessToken);
      return axiosInstance.delete(url, { data, ...axiosConfig });
    }

    const axiosInstance = await getAxiosInstance(baseUrlKey);
    return axiosInstance.delete(arg1, { data });
  },
};

export default apiService;
